import { useState, useEffect, useCallback, useMemo } from "react";
import { useSubscription } from "../context/SubscriptionContext";
import {
  BarChart2, Sparkles, LayoutList, CalendarDays, Settings2,
  Activity, FileText, CheckCircle2, XCircle, Building2,
  ChevronDown, ChevronRight,
  PanelLeft, PanelLeftClose, LogOut, UserCircle,
  Plus, Trash2, CreditCard, Globe, Wallet, FileSignature, ClipboardCheck, Handshake, Clock, Package } from "lucide-react";
import { TOKENS } from "../tokens";
import { useWindowWidth } from "../hooks/useWindowWidth";

function relDate(str) {
  if (!str) return "";
  const d = new Date(str);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const FH = TOKENS.fontHeading;
const FU = TOKENS.fontUI;


const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard",      Icon: BarChart2,    view: "dashboard" },
  { id: "extract",   label: "Grant Extractor", Icon: Sparkles,     view: "extract"   },
  { id: "discover",  label: "Discover Grants", Icon: Globe,        view: "discover"  },
  {
    id: "grantOS",  label: "NofoDesk",          Icon: LayoutList,   expandable: true,
    children: [
      { id: "myGrants",    label: "My Grants",    Icon: UserCircle,    view: "tracker", subTab: "myGrants"   },
      { id: "active",      label: "Active",       Icon: Activity,      view: "tracker", subTab: "active"     },
      { id: "apps",        label: "Applications", Icon: FileText,      view: "tracker", subTab: "applications" },
      { id: "completed",   label: "Completed",    Icon: CheckCircle2,  view: "tracker", subTab: "completed"  },
      { id: "terminated",  label: "Terminated",   Icon: XCircle,       view: "tracker", subTab: "terminated" },
      { id: "funders",     label: "Funders",      Icon: Building2,     view: "tracker", subTab: "funders"    },
      { id: "recyclebin",  label: "Recycle Bin",  Icon: Trash2,        view: "tracker", subTab: "recyclebin" },
    ],
  },
  { id: "budgets",   label: "Budgets",         Icon: Wallet,        view: "budgets"   },
  { id: "awards",    label: "Award Setup",     Icon: FileSignature, view: "awards"    },
  { id: "findings",  label: "Audit Findings",  Icon: ClipboardCheck, view: "findings" },
  { id: "subrecipients", label: "Subrecipients", Icon: Handshake, view: "subrecipients" },
  { id: "effort",    label: "Time and Effort", Icon: Clock,        view: "effort"    },
  { id: "property",  label: "Property and Procurement", Icon: Package, view: "property" },
  { id: "calendar",  label: "Calendar",        Icon: CalendarDays, view: "calendar"  },
  { id: "settings",  label: "Settings",        Icon: Settings2,    view: "settings"  },
  { id: "pricing",   label: "Plans & Billing", Icon: CreditCard,   view: "pricing"   },
];

export default function Sidebar({
  activeView,
  setActiveView,
  gosSubTab,
  setGosSubTab,
  authUser,
  sidebarOpen,
  setSidebarOpen,
  themeObj,
  themeMode,
  setThemeMode,
  navCounts,
  onLogout,
  sessions = [],
  currentSessionId,
  onNewChat,
  onLoadSession,
  onDeleteSession,
  onboarding = null,
  onOnboardingItem,
  onOnboardingDismiss,
}) {
  const windowWidth = useWindowWidth();
  const isDesktop = windowWidth >= 1024;
  const { canTeamWorkspace, plan, beta } = useSubscription();

  // Founding-user path (ONBOARDING_SPEC §3): the Supabase flip sets the
  // beta flag; paid plans without it read as plain full access.
  const founding   = !!beta;
  const fullAccess = founding || ['pro', 'org', 'beta'].includes(plan);

  const NAV = useMemo(() => NAV_ITEMS.map(item => {
    if (item.children) {
      return { ...item, children: item.children.filter(c => c.id !== 'myGrants' || canTeamWorkspace) };
    }
    return item;
  }), [canTeamWorkspace]);

  /* On desktop: sidebarOpen=true → 240px, false → 72px.
     On mobile:  sidebarOpen=true → drawer visible.       */
  const isCollapsed = isDesktop && !sidebarOpen;

  const isOnTracker = activeView === "tracker";
  const [grantOsOpen, setGrantOsOpen] = useState(isOnTracker);

  useEffect(() => { if (isOnTracker) setGrantOsOpen(true); }, [isOnTracker]);

  const navigate = useCallback((view, subTab) => {
    setActiveView(view);
    if (subTab !== undefined) setGosSubTab(subTab);
    if (!isDesktop) setSidebarOpen(false);
  }, [setActiveView, setGosSubTab, isDesktop, setSidebarOpen]);

  const isActive = (item) =>
    item.view === "tracker"
      ? activeView === "tracker" && gosSubTab === item.subTab
      : activeView === item.view;

  const initial = (authUser?.name || authUser?.email || "U")[0].toUpperCase();
  const [userRole, setUserRole] = useState(() => localStorage.getItem("nofodesk_role") || "Grants Manager");
  const [editingRole, setEditingRole] = useState(false);

  /* ── Shared colours (theme-aware) ────────────────────── */
  const sb     = themeObj || {};
  const bg     = sb.sbBg     || "#0A1628";
  const border = sb.sbBorder || TOKENS.border;
  const textP  = sb.sbText   || TOKENS.textPrimary;
  const textM  = sb.sbMuted  || TOKENS.textMuted;
  const hoverBg  = sb.sbHover  || "rgba(30,58,95,0.5)";
  const activeBg = sb.sbActive || TOKENS.bgElevated;
  const gold     = sb.gold     || TOKENS.gold;
  // Hover text. The dark palette's pale lavender is near-invisible on a white
  // sidebar, so light mode uses its body text; dark modes are unchanged.
  const textHover = sb.mode === "light" ? (sb.textBody || textP) : TOKENS.textSecondary;

  /* ── Sidebar container style ─────────────────────────── */
  const sidebarStyle = isDesktop
    ? {
        width: isCollapsed ? 72 : 240,
        flexShrink: 0,
        transition: "width 0.22s ease",
        background: bg,
        borderRight: `1px solid ${border}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100vh",
        position: "relative",
      }
    : {
        position: "fixed",
        top: 0, left: 0, bottom: 0,
        width: 240,
        zIndex: 500,
        background: bg,
        borderRight: `1px solid ${border}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.25s ease",
      };

  /* ── Base button style (shared by nav items) ─────────── */
  const itemBase = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: FU,
    fontSize: 13,
    lineHeight: 1.4,
    transition: "background 0.12s, color 0.12s",
    position: "relative",
    borderRadius: 6,
  };

  /* ── NavItem: single nav button ──────────────────────── */
  function NavItem({ item }) {
    const active = isActive(item);
    const [hov, setHov] = useState(false);
    const indent = item.subTab !== undefined; /* child of Grant OS */

    return (
      <button
        onClick={() => navigate(item.view, item.subTab)}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        title={isCollapsed ? item.label : undefined}
        aria-current={active ? "page" : undefined}
        data-tour={`nav-${item.id}`}
        style={{
          ...itemBase,
          gap: isCollapsed ? 0 : indent ? 8 : 10,
          padding: isCollapsed
            ? "9px 0"
            : indent ? "7px 12px 7px 36px" : "9px 12px",
          justifyContent: isCollapsed ? "center" : "flex-start",
          background: active ? activeBg : hov ? hoverBg : "transparent",
          color: active ? textP : hov ? textHover : textM,
          /* Inset left gold accent for active state */
          boxShadow: active ? `inset 3px 0 0 ${gold}` : "none",
          borderRadius: active ? "0 6px 6px 0" : 6,
        }}
      >
        <span style={{ flexShrink: 0, display: "flex", opacity: active ? 1 : 0.75 }}>
          <item.Icon size={indent ? 14 : 20} strokeWidth={1.5} />
        </span>

        {!isCollapsed && (
          <>
            <span style={{ flex: 1 }}>{item.label}</span>
            {navCounts?.[item.id] > 0 && (
              <CountBadge count={navCounts[item.id]} variant={item.id === "active" ? "green" : "amber"} />
            )}
          </>
        )}

        {/* Collapsed dot badge */}
        {isCollapsed && navCounts?.[item.id] > 0 && (
          <span style={{
            position: "absolute", top: 4, right: 6,
            width: 14, height: 14, borderRadius: "50%",
            background: item.id === "active" ? TOKENS.statusGreen : TOKENS.statusAmber,
            color: "#fff", fontSize: 8, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {navCounts[item.id] > 9 ? "9+" : navCounts[item.id]}
          </span>
        )}
      </button>
    );
  }

  /* ── GrantOS expandable section ──────────────────────── */
  function GrantOsSection() {
    const [hov, setHov] = useState(false);
    const parentActive = activeView === "tracker";

    const handleClick = () => {
      if (isCollapsed) { navigate("tracker", "active"); return; }
      setGrantOsOpen(o => !o);
    };

    return (
      <div>
        <button
          onClick={handleClick}
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          aria-expanded={grantOsOpen && !isCollapsed}
          title={isCollapsed ? "Portfolio" : undefined}
          style={{
            ...itemBase,
            gap: isCollapsed ? 0 : 10,
            padding: isCollapsed ? "9px 0" : "9px 12px",
            justifyContent: isCollapsed ? "center" : "flex-start",
            background: parentActive && !grantOsOpen ? activeBg : hov ? hoverBg : "transparent",
            color: parentActive ? textP : hov ? textHover : textM,
            boxShadow: parentActive && !grantOsOpen ? `inset 3px 0 0 ${gold}` : "none",
            borderRadius: parentActive && !grantOsOpen ? "0 6px 6px 0" : 6,
          }}
        >
          <span style={{ flexShrink: 0, display: "flex", opacity: parentActive ? 1 : 0.75 }}>
            <LayoutList size={20} strokeWidth={1.5} />
          </span>
          {!isCollapsed && (
            <>
              <span style={{ flex: 1 }}>Portfolio</span>
              <span style={{ display: "flex", color: textM, opacity: 0.6 }}>
                {grantOsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            </>
          )}
        </button>

        {/* Sub-items */}
        {grantOsOpen && !isCollapsed && (
          <div style={{ marginTop: 2, marginBottom: 2 }}>
            {NAV.find(n => n.id === "grantOS").children.map(child => (
              <NavItem key={child.id} item={child} />
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── CountBadge ──────────────────────────────────────── */
  function CountBadge({ count, variant }) {
    const colors = {
      green: { bg: "rgba(16,185,129,0.15)", text: TOKENS.statusGreen, bd: "rgba(16,185,129,0.3)" },
      amber: { bg: "rgba(245,158,11,0.15)",  text: TOKENS.statusAmber, bd: "rgba(245,158,11,0.3)" },
    };
    const c = colors[variant] || colors.amber;
    return (
      <span style={{
        fontSize: 10, fontWeight: 600, padding: "1px 6px",
        borderRadius: 9999, background: c.bg, color: c.text,
        border: `1px solid ${c.bd}`, fontFamily: FU,
      }}>
        {count}
      </span>
    );
  }

  /* ── Separator ───────────────────────────────────────── */
  function NavSep() {
    return (
      <div style={{
        height: 1, background: border, opacity: 0.5,
        margin: isCollapsed ? "6px 12px" : "6px 8px",
      }} />
    );
  }

  /* ── Session history item ────────────────────────────── */
  function SessionItem({ session, active }) {
    const [hov, setHov] = useState(false);
    const [hovDel, setHovDel] = useState(false);
    return (
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => { setHov(false); setHovDel(false); }}
        style={{ position: "relative", borderRadius: 6, marginBottom: 1 }}
      >
        <button
          onClick={() => onLoadSession(session)}
          style={{
            ...itemBase,
            padding: "6px 32px 6px 12px",
            gap: 8,
            width: "100%",
            background: active ? activeBg : hov ? hoverBg : "transparent",
            color: active ? textP : textM,
            boxShadow: active ? `inset 3px 0 0 ${gold}` : "none",
            borderRadius: "0 6px 6px 0",
          }}
        >
          <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            <div style={{ fontSize: 12, fontFamily: FU, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: active ? textP : textM }}>
              {session.title || "Untitled"}
            </div>
            <div style={{ fontSize: 10, color: textM, opacity: 0.55, marginTop: 1, fontFamily: FU }}>{relDate(session.updatedAt)}</div>
          </div>
        </button>
        {hov && (
          <button
            onMouseEnter={() => setHovDel(true)}
            onMouseLeave={() => setHovDel(false)}
            onClick={(e) => { e.stopPropagation(); onDeleteSession(session.id); }}
            title="Delete"
            style={{
              position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
              padding: 4, background: hovDel ? "rgba(220,38,38,0.15)" : "transparent",
              border: "none", borderRadius: 4, cursor: "pointer",
              color: hovDel ? "#ef4444" : textM, display: "flex", alignItems: "center", opacity: 0.7,
            }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    );
  }

  /* ── Onboarding checklist (ONBOARDING_SPEC §2 Beat 5) ──
     Sidebar-resident, 4 items, mono labels, thin accent progress bar,
     dismissible forever, auto-hides after the quiet completion state. */
  function OnboardingChecklist() {
    const o = onboarding;
    if (!o || o.dismissed || isCollapsed) return null;
    const items = [
      { id: "sample",   label: "See a sample extraction",            done: !!o.sampleViewed },
      { id: "real",     label: "Extract a grant you're working on",  done: !!o.firstRealExtraction, weighted: true },
      { id: "profile",  label: founding ? "Check your eligibility (included in your founding access)" : "Check your eligibility (requires mini-profile)", done: !!o.profileDone },
      { id: "deadline", label: "Save a deadline to your tracker",    done: !!o.deadlineSaved },
    ];
    const done = items.filter(i => i.done).length;
    const allDone = done === items.length;

    if (allDone) {
      return (
        <div style={{ margin: "0 8px 8px", padding: "10px 12px", border: `1px solid ${border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 11, color: textM, fontFamily: TOKENS.fontMono, letterSpacing: "0.04em" }}>You're set up.</span>
          <button onClick={onOnboardingDismiss} aria-label="Dismiss setup checklist" style={{ background: "none", border: "none", color: textM, fontSize: 14, cursor: "pointer", lineHeight: 1, padding: 2 }}>×</button>
        </div>
      );
    }

    return (
      <div style={{ margin: "0 8px 8px", border: `1px solid ${border}`, borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px 6px" }}>
          <span style={{ fontSize: 10, color: textM, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: TOKENS.fontMono }}>Getting started</span>
          <button onClick={onOnboardingDismiss} aria-label="Dismiss setup checklist" title="Dismiss forever" style={{ background: "none", border: "none", color: textM, fontSize: 14, cursor: "pointer", lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={{ height: 2, background: border, margin: "0 12px" }}>
          <div style={{ height: "100%", width: `${(done / items.length) * 100}%`, background: gold, transition: "width 0.4s ease" }} />
        </div>
        <div style={{ padding: "6px 4px 8px" }}>
          {items.map(item => (
            <button
              key={item.id}
              onClick={() => !item.done && onOnboardingItem?.(item.id)}
              style={{
                display: "flex", alignItems: "flex-start", gap: 8, width: "100%",
                padding: "5px 8px", background: "none", border: "none",
                cursor: item.done ? "default" : "pointer", textAlign: "left", borderRadius: 5,
              }}
            >
              <span style={{
                width: 13, height: 13, borderRadius: 3, flexShrink: 0, marginTop: 1,
                background: item.done ? gold : "transparent",
                border: `1.5px solid ${item.done ? gold : border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {item.done && <span style={{ color: "#fff", fontSize: 8, lineHeight: 1 }}>✓</span>}
              </span>
              <span style={{
                fontFamily: TOKENS.fontMono, fontSize: 10, letterSpacing: "0.02em", lineHeight: 1.5,
                color: item.done ? textM : item.weighted ? textP : textHover,
                fontWeight: item.weighted && !item.done ? 600 : 400,
                textDecoration: item.done ? "line-through" : "none",
              }}>{item.label}</span>
            </button>
          ))}
          {/* Replay the product tour. The tour's closing step promises this
              exists, so it has to actually be reachable from the UI. */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("nofodesk:start-tour"))}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%",
              padding: "6px 8px 2px", background: "none", border: "none", cursor: "pointer",
              textAlign: "left", borderRadius: 5,
              fontFamily: TOKENS.fontMono, fontSize: 10, letterSpacing: "0.02em",
              color: textM,
            }}
          >
            <span style={{ width: 13, flexShrink: 0, textAlign: "center", fontSize: 10 }}>↻</span>
            <span>Replay the tour</span>
          </button>
        </div>
      </div>
    );
  }

  /* ── Render ──────────────────────────────────────────── */
  return (
    <>
      {/* Mobile backdrop */}
      {!isDesktop && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 499,
          }}
        />
      )}

      <aside style={sidebarStyle} aria-label="Main navigation">

        {/* ── Brand / Logo ──────────────────────────────── */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Go to Dashboard"
          onClick={() => navigate("dashboard")}
          onKeyDown={e => e.key === "Enter" && navigate("dashboard")}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: isCollapsed ? "center" : "space-between",
            padding: isCollapsed ? "16px 0" : "14px 12px 14px 16px",
            borderBottom: `1px solid ${border}`,
            cursor: "pointer",
            flexShrink: 0,
            userSelect: "none",
          }}
        >
          {/* Logo mark */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: gold, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ color: "#0A1628", fontSize: 15, fontWeight: 700, fontFamily: FH, lineHeight: 1 }}>N</span>
            </div>
            {!isCollapsed && (
              <span style={{
                fontSize: 18, fontFamily: FH, color: textP,
                letterSpacing: "-0.01em", lineHeight: 1,
              }}>
                Nofo<span style={{ color: gold }}>Desk</span>
              </span>
            )}
          </div>

          {/* Collapse toggle (desktop only) */}
          {isDesktop && !isCollapsed && (
            <button
              title="Collapse sidebar"
              onClick={e => { e.stopPropagation(); setSidebarOpen(false); }}
              style={{
                padding: 4, background: "transparent", border: "none",
                color: textM, cursor: "pointer", borderRadius: 4,
                display: "flex", alignItems: "center", opacity: 0.6,
                transition: "opacity 0.15s",
                flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0.6}
            >
              <PanelLeftClose size={16} />
            </button>
          )}
        </div>

        {/* ── Navigation ───────────────────────────────── */}
        <nav
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: isCollapsed ? "8px 6px" : "8px 8px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div>
            {NAV.map(item => {
              if (item.expandable) return <GrantOsSection key={item.id} />;
              if (item.id === "calendar") return (
                <div key={item.id}>
                  <NavSep />
                  <NavItem item={item} />
                </div>
              );
              return <NavItem key={item.id} item={item} />;
            })}
          </div>

          {/* ── Chat history (extract view only) ──────── */}
          {activeView === "extract" && !isCollapsed && (
            <div style={{ marginTop: 8, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <NavSep />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 8px 2px" }}>
                <span style={{ fontSize: 10, color: textM, textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.55 }}>Extractions</span>
                <button
                  onClick={onNewChat}
                  title="New extraction"
                  style={{ padding: "3px 6px", background: "transparent", border: `1px solid ${border}`, borderRadius: 5, color: gold, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}
                >
                  <Plus size={12} />
                  <span style={{ fontFamily: FU }}>New</span>
                </button>
              </div>
              <div style={{ flex: 1, overflowY: "auto", marginTop: 4 }}>
                {sessions.length === 0 && (
                  <div style={{ padding: "8px 12px", fontSize: 11, color: textM, opacity: 0.5, fontFamily: FU }}>No extractions yet</div>
                )}
                {sessions.map(s => (
                  <SessionItem key={s.id} session={s} active={s.id === currentSessionId} />
                ))}
              </div>
            </div>
          )}
        </nav>

        {/* ── Expand button (collapsed desktop state) ───── */}
        {isDesktop && isCollapsed && (
          <div style={{ padding: "6px", borderTop: `1px solid ${border}`, flexShrink: 0 }}>
            <button
              title="Expand sidebar"
              onClick={() => setSidebarOpen(true)}
              style={{
                width: "100%", padding: "8px 0",
                background: "transparent", border: "none",
                color: textM, cursor: "pointer", borderRadius: 6,
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: 0.6, transition: "opacity 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0.6}
            >
              <PanelLeft size={18} />
            </button>
          </div>
        )}

        {/* ── Onboarding checklist ──
            Called as a function, not <JSX/>: as a nested component its
            identity would change every render and React would remount the
            whole subtree (killing the progress-bar transition). No hooks
            inside, so a plain call is safe. */}
        {OnboardingChecklist()}

        {/* ── Theme toggle ─────────────────────────────── */}
        {!isCollapsed && setThemeMode && (
          <div style={{ padding: "8px 12px", flexShrink: 0 }}>
            <div style={{
              display: "flex", borderRadius: 6, overflow: "hidden",
              border: `1px solid ${border}`, background: bg,
            }}>
              {[{ id: "navy", label: "Navy" }, { id: "black", label: "Black" }, { id: "light", label: "Light" }].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setThemeMode(opt.id)}
                  style={{
                    flex: 1, padding: "5px 0", fontSize: 11, fontFamily: FU, fontWeight: 500,
                    border: "none", cursor: "pointer", transition: "background 0.15s, color 0.15s",
                    background: themeMode === opt.id ? gold : "transparent",
                    color: themeMode === opt.id ? "#F0EFFF" : textM,
                    borderRadius: 0,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── User profile ──────────────────────────────── */}
        {authUser && (
          <div style={{
            borderTop: `1px solid ${border}`,
            padding: isCollapsed ? "10px 6px" : "10px 12px",
            flexShrink: 0,
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: isCollapsed ? 0 : 10,
              justifyContent: isCollapsed ? "center" : "flex-start",
            }}>
              {/* Avatar */}
              <div style={{
                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                background: "linear-gradient(135deg, #1c6cbe, #7c3aed)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, color: "#fff", fontWeight: 600,
              }}>
                {initial}
              </div>

              {!isCollapsed && (
                <>
                  <div style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, color: textP, fontFamily: FU, fontWeight: 500,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {authUser.name || authUser.email}
                    </div>
                    {editingRole ? (
                      <input
                        autoFocus
                        value={userRole}
                        onChange={e => setUserRole(e.target.value)}
                        onBlur={() => { localStorage.setItem("nofodesk_role", userRole); setEditingRole(false); }}
                        onKeyDown={e => { if (e.key === "Enter" || e.key === "Escape") { localStorage.setItem("nofodesk_role", userRole); setEditingRole(false); } }}
                        style={{
                          fontSize: 11, color: textM, fontFamily: FU, marginTop: 1,
                          background: "transparent", border: "none",
                          borderBottom: `1px solid ${gold}`, outline: "none",
                          width: "100%", padding: 0,
                        }}
                      />
                    ) : (
                      <div
                        onClick={() => setEditingRole(true)}
                        title="Click to edit your role"
                        style={{ fontSize: 11, color: textM, fontFamily: FU, marginTop: 1, cursor: "pointer" }}
                      >
                        {userRole}
                      </div>
                    )}
                    {fullAccess && (
                      <div style={{
                        marginTop: 4, display: "inline-flex", alignItems: "center",
                        fontSize: 9, padding: "2px 7px", borderRadius: 4,
                        fontFamily: TOKENS.fontMono, letterSpacing: "0.04em",
                        background: "rgba(91,79,233,0.12)", color: TOKENS.slateBlue,
                        border: "1px solid rgba(91,79,233,0.3)", whiteSpace: "nowrap",
                      }}>
                        {founding ? "Founding user — full access" : "Full access active"}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 3, flexShrink: 0, alignItems: "center" }}>
                    {!authUser.devMode && (
                      <button
                        title="Sign out"
                        onClick={onLogout}
                        style={{
                          padding: 5, background: "transparent", border: "none",
                          color: textM, cursor: "pointer", borderRadius: 4,
                          display: "flex", alignItems: "center", opacity: 0.7,
                          transition: "opacity 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
                      >
                        <LogOut size={14} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Legal line — names the company behind the product on every
             signed-in screen without taking space from any of them. Hidden
             on the collapsed icon rail, which has no room for a sentence. ── */}
        {!isCollapsed && (
          <div style={{
            padding: "0 12px 10px", flexShrink: 0,
            fontSize: 10, lineHeight: 1.45, color: textM, fontFamily: FU, opacity: 0.75,
          }}>
            © {new Date().getFullYear()} FOKT Consulting LLC. NofoDesk is a product of{" "}
            <a href="https://foktconsulting.com" target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>FOKT Consulting LLC</a>.
          </div>
        )}
      </aside>
    </>
  );
}
